import { useEffect, useMemo, useState } from "react";
import ReactFlow, { Background, Controls, MiniMap, Handle, Position } from "reactflow";
import "reactflow/dist/style.css";
import { api, RAYG, RAYG_TEXT, EVIDENCE } from "../lib/api";
import { PageHeader } from "../components/Layout";
import { WorkshopToolbar } from "../components/WorkshopToolbar";
import { X, Presentation } from "@phosphor-icons/react";

function DeptNode({ data }) {
  return (
    <div onClick={data.onClick} data-testid={`map-dept-${data.dept.key}`}
      className="chip cursor-pointer bg-white border-2 rounded-sm w-[210px] shadow-sm" style={{ borderColor: data.dept.color }}>
      <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />
      <div className="px-3 py-2 text-white text-[12px] font-bold font-display tracking-tight" style={{ background: data.dept.color }}>
        {data.dept.name}
      </div>
      <div className="px-3 py-2 text-[11px] text-black/70 space-y-1">
        <p className="truncate">{data.dept.owner}</p>
        <div className="flex justify-between">
          <span>{data.dept.headcount} staff</span>
          <span className="font-mono">{data.capCount} caps</span>
        </div>
        <div className="flex gap-1 items-center">
          <span className="text-black/45">Maturity</span>
          {[1, 2, 3, 4, 5].map((i) => (
            <span key={i} className={`w-2 h-2 rounded-full ${i <= data.dept.maturity_current ? "" : "opacity-20"}`} style={{ background: data.dept.color }} />
          ))}
        </div>
      </div>
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} />
    </div>
  );
}

const nodeTypes = { dept: DeptNode };

export default function EnterpriseMap() {
  const [depts, setDepts] = useState([]);
  const [caps, setCaps] = useState([]);
  const [wfs, setWfs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [org, setOrg] = useState(null);
  const [workshop, setWorkshop] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/departments"), api.get("/capabilities"), api.get("/workflows"), api.get("/organization")]).then(([d, c, w, o]) => {
      setDepts(d.data); setCaps(c.data); setWfs(w.data); setOrg(o.data);
    });
  }, []);

  const { nodes, edges } = useMemo(() => {
    const nodes = [
      { id: "enterprise", position: { x: 560, y: 0 }, draggable: false,
        data: { label: "" }, style: { width: 230, background: "#0A0A0A", color: "#D4AF37", border: "2px solid #D4AF37", borderRadius: 4, padding: 12, fontFamily: "Cabinet Grotesk", fontWeight: 800, fontSize: 15, textAlign: "center" },
      },
    ];
    nodes[0].data.label = `${org?.name || "Enterprise"} — Enterprise`;
    depts.forEach((d, i) => {
      nodes.push({ id: d.key, type: "dept", position: { x: (i % 5) * 260 + 30, y: Math.floor(i / 5) * 240 + 130 },
        data: { dept: d, capCount: caps.filter((c) => c.department_key === d.key).length, onClick: () => setSelected(d) } });
    });
    const edges = depts.map((d) => ({ id: `e-${d.key}`, source: "enterprise", target: d.key, style: { stroke: d.color, strokeWidth: 1.5 }, animated: false }));
    const seen = new Set();
    wfs.forEach((w) => {
      for (let i = 0; i < w.departments.length - 1; i++) {
        const key = `${w.departments[i]}-${w.departments[i + 1]}`;
        if (!seen.has(key)) {
          seen.add(key);
          edges.push({ id: `wf-${key}`, source: w.departments[i], target: w.departments[i + 1], animated: true, style: { stroke: "#D4AF37", strokeWidth: 1, opacity: 0.5 } });
        }
      }
    });
    return { nodes, edges };
  }, [depts, caps, wfs, org]);

  const selCaps = selected ? caps.filter((c) => c.department_key === selected.key) : [];
  const selWfs = selected ? wfs.filter((w) => w.departments.includes(selected.key)) : [];

  return (
    <div className="min-h-screen flex flex-col" data-testid="enterprise-map-page">
      <PageHeader overline="Enterprise Map" title="Enterprise Function Map"
        subtitle="Colour-coded departments connected to the enterprise. Gold animated links show cross-functional workflow hand-offs. Click a department to drill down."
        actions={<button onClick={() => setWorkshop(true)} data-testid="workshop-mode-btn"
          className="flex items-center gap-1.5 text-xs bg-[#0A0A0A] text-[#D4AF37] rounded-sm px-4 py-2 font-semibold hover:bg-black/85 transition-colors"><Presentation size={14} /> Workshop mode</button>} />
      {workshop && <WorkshopToolbar title="Enterprise Function Map" context="map" onExit={() => setWorkshop(false)} />}
      <div className={workshop ? "fixed inset-0 top-[52px] z-40 bg-white" : "mx-8 mb-8 border border-black/10 rounded-sm bg-white relative"}
        style={workshop ? {} : { height: "68vh", width: "auto" }}>
        {depts.length > 0 && (
        <ReactFlow key={workshop ? "ws" : "std"} nodes={nodes} edges={edges} nodeTypes={nodeTypes} fitView proOptions={{ hideAttribution: true }} style={{ width: "100%", height: "100%" }}>
          <Background color="#00000012" gap={22} />
          <Controls />
          <MiniMap nodeColor={(n) => (n.type === "dept" ? n.data.dept.color : "#0A0A0A")} maskColor="rgba(253,251,247,0.7)" />
        </ReactFlow>
        )}
        {selected && (
          <div className="absolute inset-y-0 right-0 w-[380px] bg-white border-l border-black/10 shadow-xl overflow-y-auto z-10 fade-up" data-testid="dept-detail-panel">
            <div className="p-4 text-white sticky top-0" style={{ background: selected.color }}>
              <div className="flex justify-between items-start">
                <h2 className="font-display font-bold text-lg tracking-tight pr-4">{selected.name}</h2>
                <button onClick={() => setSelected(null)} data-testid="close-dept-panel-btn" className="hover:opacity-70 transition-opacity"><X size={18} /></button>
              </div>
              <p className="text-[12px] opacity-90 mt-1">{selected.purpose}</p>
            </div>
            <div className="p-4 space-y-4 text-[13px]">
              <div>
                <p className="text-[10px] uppercase tracking-[0.15em] text-black/50">Executive owner</p>
                <p className="font-medium">{selected.owner} · {selected.headcount} staff</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.15em] text-black/50 mb-1">Outcomes supported</p>
                {selected.outcomes.map((o) => <span key={o} className="inline-block text-[11px] border border-[#D4AF37]/60 rounded-full px-2 py-0.5 mr-1 mb-1">{o}</span>)}
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.15em] text-black/50 mb-1">Capabilities</p>
                {selCaps.map((c) => (
                  <div key={c.id} className="flex items-center justify-between py-1.5 border-b border-black/5" data-testid="dept-capability-row">
                    <div>
                      <p>{c.name}</p>
                      <span className="text-[10px]" style={{ color: EVIDENCE[c.evidence_status]?.color }}>● {EVIDENCE[c.evidence_status]?.label}</span>
                    </div>
                    <div className="text-right">
                      <span className="text-[11px] font-mono px-1.5 py-0.5 rounded-sm" style={{ background: RAYG(c.ai_opportunity), color: RAYG_TEXT(c.ai_opportunity) }}>AI {c.ai_opportunity}</span>
                      <p className="text-[10px] text-black/45 mt-0.5">maturity {c.maturity_current}→{c.maturity_target}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.15em] text-black/50 mb-1">Cross-functional workflows</p>
                {selWfs.map((w) => <p key={w.id} className="py-1 border-b border-black/5">{w.name} <span className="text-[10px] font-mono px-1 rounded-sm ml-1" style={{ background: RAYG(w.health), color: RAYG_TEXT(w.health) }}>{w.health}</span></p>)}
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.15em] text-black/50 mb-1">Pain points</p>
                {selected.pain_points.map((p) => <p key={p} className="text-black/70">— {p}</p>)}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
